import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/lib/apiError';
import { esAdmin } from '@/lib/roles';
import { lugar } from '@/lib/lugar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Package, Plus, ArrowRight, MapPin, Truck, 
  Loader2, Calendar, DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShipmentsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Un error se muestra como error, no como "No shipments yet" (revision del 26-oct, E.6).
  const [loadError, setLoadError] = useState('');
  const { getAuthHeaders, user } = useAuth();
  // Verification esta fuera del menu del comerciante (§9.121): el boton de cada
  // envio lo ve solo el admin, igual que la tarjeta del Overview (§9.159).
  const verAdmin = esAdmin(user);

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await axios.get(`${API}/shipments`, {
          headers: getAuthHeaders()
        });
        setShipments(response.data);
        setLoadError('');
      } catch (error) {
        console.error('Failed to fetch shipments:', error);
        setLoadError(apiErrorMessage(error.response?.data, 'Could not load your shipments. Please try again.'));
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [getAuthHeaders]);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="shipments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Shipments</h1>
          <p className="text-slate-500 mt-1">Manage and track all your shipments</p>
        </div>
        <Link to="/dashboard/rate-shop">
          <Button className="btn-press" data-testid="new-shipment-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Shipment
          </Button>
        </Link>
      </div>

      {/* Shipments List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : loadError ? (
        <p className="text-sm text-red-600 py-12 text-center" data-testid="shipments-error">{loadError}</p>
      ) : shipments.length > 0 ? (
        <div className="grid gap-4">
          {shipments.map((shipment) => (
            <Card key={shipment.id} className="hover:shadow-md transition-shadow" data-testid={`shipment-card-${shipment.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <code className="font-mono text-sm text-slate-600 dark:text-slate-400">
                          {shipment.id.slice(0, 8)}...
                        </code>
                        <span className={`status-badge status-${shipment.status || 'unknown'}`}>
                          {shipment.status || '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {lugar(shipment.ship_from)} → {lugar(shipment.ship_to)}
                        </span>
                      </div>
                      {shipment.carrier && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <Truck className="w-4 h-4" />
                          <span>{shipment.carrier} - {shipment.service}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-slate-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        {shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : '—'}
                      </div>
                      <div className="flex items-center gap-1 text-lg font-semibold tabular-nums mt-1">
                        <DollarSign className="w-4 h-4" />
                        {shipment.total_cost == null ? '—' : Number(shipment.total_cost).toFixed(2)}
                      </div>
                    </div>
                    {verAdmin && (
                    <Link to={`/dashboard/verification?shipment=${shipment.id}`}>
                      <Button variant="outline" size="sm" data-testid={`verify-shipment-${shipment.id}`}>
                        Verify
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    )}
                  </div>
                </div>

                {/* Coverage badges */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                  {shipment.coverage?.prefraud && (
                    <span className="text-xs px-2 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-full">
                      Prefraud
                    </span>
                  )}
                  {shipment.coverage?.verify_transaction && (
                    <span className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full">
                      Verified
                    </span>
                  )}
                  {shipment.coverage?.basic_coverage && (
                    <span className="text-xs px-2 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-full">
                      Basic Coverage
                    </span>
                  )}
                  {shipment.coverage?.full_coverage && (
                    <span className="text-xs px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-full">
                      Full Coverage
                    </span>
                  )}
                  {shipment.verification_score && (
                    <span className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full font-mono">
                      Score: {(shipment.verification_score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium mb-2">No Shipments Yet</h3>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
              Create your first shipment to start shipping with verification.
            </p>
            <Link to="/dashboard/rate-shop">
              <Button data-testid="first-shipment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Shipment
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ShipmentsPage;
